import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { constants } from '../shared/constants/constants';
import { BaseService } from './base-service';
import * as moment from 'moment';
import { ApplicationService } from './application.service';
import { UserService } from './user.service';
import { MetaResponse } from '../shared/interfaces/meta-response';

@Injectable({
    providedIn: 'root',
})
export class InstantaneousService extends BaseService {
    onConsumptionAlertDataUpdate = new BehaviorSubject<MetaResponse>(null);

    private timerSub: Subscription = null;
    private consumptionAlertTimerSub: Subscription = null;

    private updateRate = 10000;
    private latestConsumptionAlertData = null;

    constructor(
        protected http: HttpClient,
        protected auth: ApiService,
        protected user: UserService,
        private application: ApplicationService
    ) {
        super(http, auth, user);
    }

    destroy(): void {
        super.destroy();
        if (this.timerSub) {
            this.timerSub.unsubscribe();
            delete this.timerSub;
        }
        if (this.consumptionAlertTimerSub) {
            this.consumptionAlertTimerSub.unsubscribe();
            delete this.consumptionAlertTimerSub;
        }
    }

    /**
     * Start cyclic consumption alert value update
     */
    startConsumptionAlertValueLiveUpdate(maxOffset): void {
        if (this.consumptionAlertTimerSub) {
            return;
        }
        const now = moment();
        const to = moment(now).add(5, 'minutes').toDate().toUTCString().toString();
        const from = moment(now)
            .subtract(maxOffset + 1, 'days')
            .toDate()
            .toUTCString()
            .toString();

        this.consumptionAlertTimerSub = timer(0, this.updateRate)
            .pipe(mergeMap(() => this.requestDataForTimeframe(from, to)))
            .subscribe({
                next: (res: MetaResponse) => {
                    this.onConsumptionAlertDataUpdate.next(res);
                },
            });
    }

    /**
     * Request instantaneous data for a specific timeframe
     */
    // this
    private requestDataForTimeframe(start: string, end: string): Observable<MetaResponse> {
        let url =
            this.API_BASE_URL +
            constants.api.routes.instantaneousPower +
            `/${start}/${end}/900/max`;
        if (this.application.isDemoMode()) {
            url = `assets/data/demo/${constants.demo.files.instantaneousPower}.json`;
        }
        return this.http.get(url).pipe(
            mergeMap((res: { status: string; data: any }) => of(this.mapDefault(res))),
            mergeMap((mappedResponse: any) => {
                if (!this.application.isDemoMode()) {
                    if (!mappedResponse) {
                        return this.mapToMetaResponse(mappedResponse, true);
                    }
                    this.latestConsumptionAlertData = mappedResponse;
                    return this.mapToMetaResponse(mappedResponse, false);
                }
                return this.mapToMetaResponse(
                    this.alignConsumptionAlertPowerDataForDemoMode(mappedResponse),
                    false
                );
            }),
            catchError((error) => {
                return this.mapToMetaResponse(error, true);
            })
        );
    }

    /**
     * Mapping function for demo mode
     */
    private alignConsumptionAlertPowerDataForDemoMode(res): any {
        const now = moment();
        const nearest = Math.floor(now.minutes() / 15) * 15;
        const tsStart = moment(now).minutes(nearest).second(0).millisecond(0);
        const reversed = res.results.slice().reverse();
        reversed.forEach((element, idx) => {
            const newTimestamp = moment(tsStart).subtract(idx * 15, 'minutes');
            element.timestamp = newTimestamp.toDate();
        });
        const results = reversed.slice().reverse();
        return { results };
    }
}
